
import { Vue, Component, Prop } from "vue-property-decorator";
import GET_PROJECTS from "@/apollo/downloadData/GetProjects.gql";
import UiButton from "@/components/button/ui-button.vue";
import DownloadIcon from "@/components/icons/DownloadBlack.vue";
import { NOTIFICATION } from "@/entities/Events";
import { Company, Project, Project_Activity } from "@/generated/graphql";
import Message from "@/data/notificationsMessages";
import { defaultErrorNotificationMessage } from "@/data/errorMessages";
import getFullNameOrPlaceholder from "@/helpers/getFullNameOrPlaceholder";

@Component({
  components: {
    UiButton,
    DownloadIcon,
  },
})
export default class ProjectCSVDownloader extends Vue {
  @Prop({ required: true }) company!: Company;

  loading = false;
  projects: Project[] | null = null;

  pattern = {
    client: "Client ID",
    projectName: "Client name",
    gender: "Gender",
    age: "Age",
    phoneNumber: "Phone number",
    emailAddress: "Email address",
    address: "Address",
    createdAt: "Date created",
    managerFullName: "Manager",
    tasks: "Total visits",
    activityRequestedHours: "Requested hours",
    activityConfirmedHours: "Confirmed hours",
    tags: "Tags",
  };

async handleDownload() {
  this.loading = true;
  console.log("🔄 handleDownload started");

  try {
    const { data } = await this.$apollo.query({
      query: GET_PROJECTS,
      variables: { company_id: this.company.id },
    });

    this.projects = data.project;

    const content = this.generateDocumentContent();

    if (content && content.length > 0) {
      this.downloadCSV(content);
    } else {
      this.$eventBus.emit(
        NOTIFICATION,
        Message.errorMessage("No data available to download.")
      );
    }
  } catch (e) {
    this.$eventBus.emit(
      NOTIFICATION,
      Message.errorMessage(defaultErrorNotificationMessage)
    );
  } finally {
    this.loading = false;
  }
}

generateDocumentContent(): Record<string, string | number>[] {
  if (!this.projects) {
    return [];
  }

  return this.projects.map(project => {
    const {
      client_id,
      client_name: projectName,
      gender,
      dob,
      manager,
      phone_number,
      email_address,
      address,
      created_at,
      project_activities,
      project_skills,
    } = project;

    const managerFullName = getFullNameOrPlaceholder(
      manager?.user.given_name,
      manager?.user.family_name!
    );
    const age = this.calculateAge(dob);
    const createdAt = this.formatDate(created_at);

    const tagsRaw = project_skills?.map(s => s.skill?.name).filter(Boolean).join(", ") || "";
    const tags = tagsRaw;

    const rowData = {
      client: client_id || "",
      projectName,
      gender,
      age,
      phoneNumber: phone_number,
      emailAddress: email_address,
      address,
      createdAt,
      managerFullName,
      tasks: project_activities?.length.toString() || "0",
      tags,
      activityRequestedHours: "", // blank
      activityConfirmedHours: "", // blank
    };

    return rowData;
  });
}


  getActivityDetails(activity: Project_Activity): Record<string, any> {
    return {
      activityRequestedHours: activity.requested_hours || "",
      activityConfirmedHours: activity.confirmed_hours || "",
    };
  }

  calculateAge(dob?: string): number | string {
    if (!dob) return "n/a";
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }

  formatDate(date?: string): string {
    return date ? new Date(date).toISOString().split("T")[0] : "";
  }

  downloadCSV(data: Record<string, string | number>[]) {
    const headersMap: Record<string, string> = this.pattern;

    const headers = Object.keys(headersMap);
    const headerLabels = Object.values(headersMap);

  if (!data.length) {
    return;
  }

    const csvRows = [
      headerLabels.join(","),
      ...data.map(row =>
        headers.map(key => {
          let value = row[key];
          if (Array.isArray(value)) value = value.join("; ");
          if (key === "createdAt" && value) {
            value = new Date(value).toISOString().split("T")[0];
          }
          const escaped = String(value ?? "").replace(/"/g, '""');
          return `"${escaped}"`;
        }).join(",")
      ),
    ];

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "Clients.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}
